import * as React from 'react';

import { zIndex } from '../tokens/zIndex';

type LayerTier = 'drawer' | 'modal' | 'alert';

interface LayerManagerContextValue {
  /** Register a layer and get its computed z-index. Call on mount. */
  register: (tier: LayerTier) => number;
  /** Unregister a layer. Call on unmount. */
  unregister: (tier: LayerTier, assignedZ: number) => void;
}

const LayerManagerContext = React.createContext<LayerManagerContextValue | null>(null);

/**
 * Provides dynamic z-index allocation for stackable layers (Drawer, Modal, AlertDialog).
 *
 * Each tier has a base z-index from `tokens/zIndex`. When a new layer mounts,
 * it gets `base + offset` where offset increments per active instance in that tier.
 * This guarantees:
 * - A second Modal always stacks above the first
 * - An AlertDialog always stacks above any Modal (base 4000 vs 3000)
 * - A Drawer always stacks below a Modal (base 2000 vs 3000)
 */
export function LayerManagerProvider({ children }: { children: React.ReactNode }) {
  const countersRef = React.useRef<Record<LayerTier, number[]>>({
    drawer: [],
    modal: [],
    alert: [],
  });

  const register = React.useCallback((tier: LayerTier): number => {
    const base = zIndex[tier];
    const active = countersRef.current[tier];
    const offset = active.length;
    const z = base + offset;
    active.push(z);
    return z;
  }, []);

  const unregister = React.useCallback((tier: LayerTier, assignedZ: number) => {
    const active = countersRef.current[tier];
    const idx = active.indexOf(assignedZ);
    if (idx !== -1) {
      active.splice(idx, 1);
    }
  }, []);

  const value = React.useMemo(() => ({ register, unregister }), [register, unregister]);

  return (
    <LayerManagerContext.Provider value={value}>
      {children}
    </LayerManagerContext.Provider>
  );
}

LayerManagerProvider.displayName = 'LayerManagerProvider';

/**
 * Hook for stackable layer components (Modal, Drawer, AlertDialog).
 * Returns a stable z-index for the lifetime of the component.
 *
 * Falls back to the static base value from `tokens/zIndex` if no
 * LayerManagerProvider is present (e.g. standalone usage without ThemeProvider).
 */
export function useLayerZIndex(tier: LayerTier): number {
  const ctx = React.useContext(LayerManagerContext);
  const [z, setZ] = React.useState<number>(() => zIndex[tier]);

  React.useEffect(() => {
    if (!ctx) return;
    const assigned = ctx.register(tier);
    setZ(assigned);
    return () => ctx.unregister(tier, assigned);
  }, [ctx, tier]);

  return z;
}
